import "@/App.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { Navbar } from "./components/Navbar";
import CookieConsent from "./components/CookieConsent";
import LazyLoader, { PageLoader, DashboardLoader } from "./components/LazyLoader";
import ScrollToTop from "./components/ScrollToTop";

// Critical pages - loaded immediately
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Verification from "./pages/Verification";
import ProfileSelection from "./pages/ProfileSelection";
import CompleteGoogleProfile from "./pages/CompleteGoogleProfile";

// Lazy loaded pages - loaded on demand
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const TutorDashboard = lazy(() => import("./pages/TutorDashboard"));
const ProviderDashboard = lazy(() => import("./pages/ProviderDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ChatRealtime = lazy(() => import("./pages/ChatRealtime"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Profile = lazy(() => import("./pages/Profile"));
const Favorites = lazy(() => import("./pages/Favorites"));
const CreateService = lazy(() => import("./pages/CreateService"));
const EditService = lazy(() => import("./pages/EditService"));

const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Help = lazy(() => import("./pages/Help"));

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes = [], requireVerified = false }) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
                <div className="w-8 h-8 border-4 border-[#4A90B2] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    // Check verification status for protected features (Google OAuth users exempt)
    if (requireVerified && needsVerification(user)) {
        return <Navigate to="/verification" state={{ from: location }} replace />;
    }
    
    // Check if user has any of the allowed types (by user_type OR by roles OR dual-profile)
    if (allowedTypes.length > 0) {
        const userRoles = user.roles || [];
        const hasAllowedType = allowedTypes.includes(user.user_type);
        const hasAllowedRole = userRoles.some(role => allowedTypes.includes(role));
        
        // Dual-profile: is_also_tutor allows access to tutor routes
        const isDualProfile = user.is_also_tutor || user.is_dual_profile;
        const tutorTypes = ['tutor', 'client'];
        const providerTypes = ['provider', 'host', 'caregiver', 'driver', 'partner'];
        const allowsTutor = allowedTypes.some(t => tutorTypes.includes(t));
        const allowsProvider = allowedTypes.some(t => providerTypes.includes(t));
        const dualProfileAllowed = isDualProfile && (allowsTutor || allowsProvider);
        
        if (!hasAllowedType && !hasAllowedRole && !dualProfileAllowed) {
            return <Navigate to="/" replace />;
        }
    }
    
    return children;
};

// Helper: check if user needs mandatory verification
const needsVerification = (user) => {
    if (!user || user.user_type === 'admin') return false;
    // Google users: email is auto-verified, but phone needs SMS verification
    if (user.auth_provider === 'google') {
        return !user.phone_verified;
    }
    return !user.email_verified || !user.phone_verified;
};

// Helper: determine correct dashboard for a user
// Unverified users MUST go to /verification first
// Dual-role users ALWAYS go to /tutor (provider only via manual switch)
const getUserDashboard = (user) => {
    if (!user) return '/login';
    
    // Google users with incomplete profile → full registration funnel
    if (user.auth_provider === 'google' && (user.profile_completed === false || !user.phone || user.phone === '' || !user.address)) {
        return '/register?from_google=true';
    }
    
    // Google users with profile complete but phone not verified → verification
    if (user.auth_provider === 'google' && !user.phone_verified) {
        return '/verification';
    }
    
    // VERIFICATION GATE (skip for Google OAuth users who already verified)
    if (needsVerification(user)) {
        return '/verification';
    }
    
    if (user.user_type === 'admin') return '/admin';
    
    // Check for dual-role: is_also_tutor flag set by upgrade flow, or has both roles
    const roles = user.roles || [];
    const isTutor = roles.includes('tutor') || user.user_type === 'tutor' || user.user_type === 'client';
    const isProvider = roles.some(r => ['host', 'caregiver', 'driver', 'partner'].includes(r)) || 
                       ['provider', 'host', 'caregiver', 'driver', 'partner'].includes(user.user_type);
    const isDualProfile = user.is_also_tutor || user.is_dual_profile || (isTutor && isProvider);
    
    if (isDualProfile) return '/tutor';
    if (isProvider) return '/provider';
    if (isTutor) return '/tutor';
    
    // Fallback based on actual user_type
    return '/tutor';
};

// Public Route (redirect if logged in, except for upgrade or Google completion)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    
    const searchParams = new URLSearchParams(location.search);
    const isUpgrade = searchParams.get('upgrade') === 'true';
    const isGoogleCompletion = searchParams.get('from_google') === 'true';
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
                <div className="w-8 h-8 border-4 border-[#4A90B2] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    
    // Allow upgrade and Google completion flows even when logged in
    if ((isUpgrade || isGoogleCompletion) && user) return children;
    
    if (user) return <Navigate to={getUserDashboard(user)} replace />;
    
    return children;
};

// Smart Landing Route - redirects logged-in users to their dashboard
const SmartLandingRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
                <div className="w-8 h-8 border-4 border-[#4A90B2] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    
    if (user) return <Navigate to={getUserDashboard(user)} replace />;
    
    return children;
};

// Layout with Navbar
const Layout = ({ children, hideNav = false }) => {
    return (
        <>
            {!hideNav && <Navbar />}
            {children}
        </>
    );
};

function AppRouter() {
    const location = useLocation();
    
    // Check URL fragment for session_id (Google OAuth callback)
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    
    return (
        <Routes>
            {/* Public Routes - Critical (no lazy) */}
            <Route path="/" element={<SmartLandingRoute><Layout><Landing /></Layout></SmartLandingRoute>} />
            <Route path="/login" element={<PublicRoute><Layout hideNav><Login /></Layout></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Layout hideNav><Register /></Layout></PublicRoute>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/select-profile" element={<Layout hideNav><ProfileSelection /></Layout>} />
            <Route path="/complete-profile" element={
                <ProtectedRoute>
                    <Layout hideNav><CompleteGoogleProfile /></Layout>
                </ProtectedRoute>
            } />
            
            {/* Legal Pages - Lazy */}
            <Route path="/terms" element={
                <Layout>
                    <Suspense fallback={<PageLoader />}>
                        <Terms />
                    </Suspense>
                </Layout>
            } />
            <Route path="/privacy" element={
                <Layout>
                    <Suspense fallback={<PageLoader />}>
                        <Privacy />
                    </Suspense>
                </Layout>
            } />
            <Route path="/ajuda" element={
                <Layout>
                    <Suspense fallback={<PageLoader />}>
                        <Help />
                    </Suspense>
                </Layout>
            } />
            <Route path="/help" element={<Navigate to="/ajuda" replace />} />
            
            {/* Verification */}
            <Route path="/verification" element={
                <ProtectedRoute>
                    <Layout hideNav><Verification /></Layout>
                </ProtectedRoute>
            } />
            
            {/* Services (public) - Lazy */}
            <Route path="/services" element={
                <Layout>
                    <Suspense fallback={<PageLoader />}>
                        <Services />
                    </Suspense>
                </Layout>
            } />
            <Route path="/services/:id" element={
                <Layout>
                    <Suspense fallback={<PageLoader />}>
                        <ServiceDetail />
                    </Suspense>
                </Layout>
            } />
            
            {/* Protected Routes - Lazy Dashboards */}
            <Route path="/tutor" element={
                <ProtectedRoute allowedTypes={['tutor', 'client']} requireVerified>
                    <Layout hideNav>
                        <Suspense fallback={<DashboardLoader />}>
                            <TutorDashboard />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/provider" element={
                <ProtectedRoute allowedTypes={['provider', 'host', 'caregiver', 'driver', 'partner']} requireVerified>
                    <Layout hideNav>
                        <Suspense fallback={<DashboardLoader />}>
                            <ProviderDashboard />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/admin" element={
                <ProtectedRoute allowedTypes={['admin']}>
                    <Layout hideNav>
                        <Suspense fallback={<DashboardLoader />}>
                            <AdminDashboard />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/chat" element={
                <ProtectedRoute>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <ChatRealtime />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/chat/:partnerId" element={
                <ProtectedRoute>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <ChatRealtime />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/bookings" element={
                <ProtectedRoute>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <Bookings />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
                <ProtectedRoute>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <Profile />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/favorites" element={
                <ProtectedRoute>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <Favorites />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/service/create" element={
                <ProtectedRoute allowedTypes={['provider', 'host', 'caregiver', 'driver', 'partner']}>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <CreateService />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/service/edit/:serviceId" element={
                <ProtectedRoute allowedTypes={['provider', 'host', 'caregiver', 'driver', 'partner']}>
                    <Layout>
                        <Suspense fallback={<PageLoader />}>
                            <EditService />
                        </Suspense>
                    </Layout>
                </ProtectedRoute>
            } />
            
            {/* Partner redirect to provider */}
            <Route path="/partner" element={<Navigate to="/provider" replace />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    // Remove Emergent branding elements injected at runtime
    // IMPORTANT: Be very specific to avoid breaking Radix UI components
    useEffect(() => {
        const removeEmergentBranding = () => {
            // Only target the specific Emergent badge element
            const emergentBadge = document.getElementById('emergent-badge');
            if (emergentBadge && emergentBadge.parentNode) {
                emergentBadge.parentNode.removeChild(emergentBadge);
            }
            
            // Remove specific Emergent links (not generic selectors)
            document.querySelectorAll('a[href*="emergent.sh"], a[href*="app.emergent"], a[href*="utm_source=emergent"]').forEach(el => {
                try {
                    // Only remove if it's a branding element (fixed position, bottom corner)
                    const style = window.getComputedStyle(el);
                    if (style.position === 'fixed') {
                        const rect = el.getBoundingClientRect();
                        // Only remove if in bottom-right corner (branding area)
                        if (rect.bottom > window.innerHeight - 100 && rect.right > window.innerWidth - 200) {
                            el.parentNode?.removeChild(el);
                        }
                    }
                } catch (e) {}
            });
            
            // Remove "Made with Emergent" text badges specifically
            document.querySelectorAll('a, div, span').forEach(el => {
                try {
                    const text = el.textContent?.trim() || '';
                    // Only exact matches for branding text
                    if (text === 'Made with Emergent' || text === 'Emergent') {
                        const style = window.getComputedStyle(el);
                        if (style.position === 'fixed') {
                            el.parentNode?.removeChild(el);
                        }
                    }
                } catch (e) {}
            });
        };
        
        // Run after page loads
        const timeoutId = setTimeout(removeEmergentBranding, 1000);
        
        // Cleanup
        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    return (
        <AuthProvider>
            <BrowserRouter>
                <ScrollToTop />
                <AppRouter />
                <Toaster 
                    position="bottom-right"
                    duration={5000}
                    toastOptions={{
                        style: {
                            background: '#fff',
                            border: '1px solid #E2E8F0',
                            borderRadius: '12px',
                        },
                    }}
                />
                <CookieConsent />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
